/* OutcomesPage — short post-action updates from real users.
   Backend: GET /api/engagement/outcomes (public verified)
            POST /api/engagement/outcomes (auth)
   Companion to PathStories: shorter, role-specific "where I am now" updates
   that are great proof for the Build Badge + Career Fit flow. */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Send, AlertTriangle, CheckCircle2, Sparkles, ShieldCheck, Trophy, ArrowRight, Loader2,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useT } from '../i18n';
import FounderNote from '../components/FounderNote';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const empty = { role_name: '', headline: '', body: '', proof_url: '' };

const OutcomesPage = () => {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/engagement/outcomes`);
        setItems(res.data?.outcomes || []);
      } catch (_) { /* noop */ }
      setLoading(false);
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await axios.post(`${API}/engagement/outcomes`, form);
      setDone(true);
      setForm(empty);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not submit.';
      if (err?.response?.status === 401) {
        setError('Your session expired — please reload the page and submit again.');
      } else {
        setError(typeof msg === 'string' ? msg : 'Validation error — please check the fields.');
      }
    } finally {
      setBusy(false);
    }
  };

  const inp = 'mt-1 w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-[15px]';

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-16">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold tracking-widest uppercase bg-teal-100 text-teal-800 px-3 py-1 rounded-full">
            <Trophy size={12} /> Outcomes
          </span>
          <h1 className="text-3xl md:text-5xl font-black text-gray-900 mt-3 leading-[1.05]" data-testid="outcomes-headline">
            {t('outcomes.title')}
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto mt-3 leading-relaxed">
            {t('outcomes.sub')}
          </p>
        </div>

        {/* Published outcomes */}
        <section className="mb-10" data-testid="outcomes-list">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={22} className="animate-spin text-teal-600" />
            </div>
          ) : items.length === 0 ? (
            <>
              <div className="bg-white border-2 border-dashed border-teal-300 rounded-3xl p-8 md:p-10 text-center max-w-2xl mx-auto" data-testid="outcomes-empty">
                <Sparkles size={28} className="text-teal-600 mx-auto mb-2" />
                <h2 className="text-lg md:text-xl font-extrabold text-gray-900 mb-1">No outcomes yet — be the first.</h2>
                <p className="text-sm md:text-base text-gray-600 max-w-md mx-auto">
                  The first verified outcomes will land here permanently. Yours could help the next student decide faster.
                </p>
              </div>
              <div className="mt-6">
                <FounderNote ctaLabel="" />
              </div>
            </>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {items.map((o) => (
                <article key={o.id || o.headline} className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`outcome-card-${o.id || ''}`}>
                  <div className="text-[11px] font-extrabold uppercase tracking-widest text-teal-700">{o.role_name}</div>
                  <h3 className="text-lg font-extrabold text-gray-900 mt-1 mb-2">{o.headline}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{o.body}</p>
                  <div className="mt-3 text-xs text-gray-500 inline-flex items-center gap-1.5">
                    <CheckCircle2 size={12} className="text-emerald-600" /> Verified · {o.user_name || 'Anonymous'}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Submit */}
        {done ? (
          <div className="bg-white rounded-3xl border-2 border-emerald-200 p-8 md:p-10 text-center" data-testid="outcomes-success">
            <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-3" />
            <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-2">{t('outcomes.submitted')}</h2>
            <button
              type="button"
              onClick={() => { setDone(false); setForm(empty); }}
              className="mt-3 inline-flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 font-bold px-5 py-2.5 rounded-full text-sm"
              data-testid="outcomes-done"
            >
              Submit another
            </button>
          </div>
        ) : (
          <div className="bg-white border-2 border-teal-200 rounded-3xl p-6 md:p-10 shadow-sm" data-testid="outcomes-form-wrap">
            <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-1">{t('outcomes.title')}</h2>
            <p className="text-sm text-gray-600 mb-5">5 minutes · We verify before publishing · You can stay anonymous.</p>

            {error && (
              <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-xl p-3 mb-4 text-sm text-rose-800" data-testid="outcomes-error">
                <AlertTriangle size={16} className="text-rose-600 mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
              <label className="block md:col-span-2">
                <span className="text-xs font-bold tracking-wider uppercase text-gray-600">{t('outcomes.role_label')} <span className="text-rose-600">*</span></span>
                <input
                  required
                  data-testid="outcome-role"
                  value={form.role_name}
                  onChange={(e) => setForm({ ...form, role_name: e.target.value })}
                  placeholder="e.g. Backend Engineer · Data Analyst · Founder · BBA — Finance"
                  className={inp}
                  maxLength={120}
                />
              </label>

              <label className="block md:col-span-2">
                <span className="text-xs font-bold tracking-wider uppercase text-gray-600">{t('outcomes.headline_label')} <span className="text-rose-600">*</span></span>
                <input
                  required
                  data-testid="outcome-headline"
                  value={form.headline}
                  onChange={(e) => setForm({ ...form, headline: e.target.value })}
                  placeholder="e.g. Final-year ECE → first paid internship at a fintech startup."
                  className={inp}
                  maxLength={180}
                />
              </label>

              <label className="block md:col-span-2">
                <span className="text-xs font-bold tracking-wider uppercase text-gray-600">{t('outcomes.body_label')} <span className="text-rose-600">*</span></span>
                <textarea
                  required
                  rows={6}
                  data-testid="outcome-body"
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="What did you do? What did the preview / Build Badge change? What happened next?"
                  className={inp}
                  maxLength={1500}
                />
                <span className="block text-[11px] text-gray-500 mt-1">{form.body.length}/1500</span>
              </label>

              <label className="block md:col-span-2">
                <span className="text-xs font-bold tracking-wider uppercase text-gray-600">{t('outcomes.proof_label')}</span>
                <input
                  type="url"
                  data-testid="outcome-proof"
                  value={form.proof_url}
                  onChange={(e) => setForm({ ...form, proof_url: e.target.value })}
                  placeholder="https://drive.google.com/… (offer letter / screenshot)"
                  className={inp}
                  maxLength={400}
                />
              </label>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
                <p className="text-xs text-gray-500 flex items-center gap-1.5">
                  <ShieldCheck size={13} className="text-emerald-600" />
                  We never publish without your final approval.
                </p>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="outcome-submit"
                  className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-extrabold px-6 py-3 rounded-full transition-colors"
                >
                  {busy ? 'Sending…' : <>{t('outcomes.submit')} <Send size={15} /></>}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Cross-link */}
        <div className="mt-10 text-center">
          <Link
            to="/build-badges"
            className="inline-flex items-center gap-2 text-teal-700 font-bold hover:text-teal-900"
            data-testid="outcomes-back-builds"
          >
            Browse Build Badge briefs <ArrowRight size={15} />
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OutcomesPage;
